import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Save } from "lucide-react";

const SKILL_LABELS = {
  ataque: "Ataque",
  passe: "Passe",
  defesa: "Defesa",
  saque: "Saque",
  bloqueio: "Bloqueio",
  toque: "Toque",
  skill: "Skill",
};

const SETTER_SKILL_LABELS = {
  toque_coordenacao: "Toque e Coordenação",
  controle_bola: "Controle da Bola",
  agilidade_agressividade: "Agilidade e Agressividade",
  compreensao_jogo: "Compreensão do Jogo",
  visao_jogo: "Visão de Jogo",
  tomada_decisao: "Tomada de Decisão",
  comunicacao: "Comunicação",
  atitude_mental: "Atitude Mental",
};

export default function EditEvaluationDialog({ evaluation, evaluators, open, onOpenChange, onSaved }) {
  const [profileType, setProfileType] = useState("jogador");
  const [skills, setSkills] = useState({});
  const [setterSkills, setSetterSkills] = useState({});
  const [evaluatorId, setEvaluatorId] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (evaluation) {
      const pt = evaluation.profile_type || "jogador";
      setProfileType(pt);
      setEvaluatorId(evaluation.evaluator_id || "");

      if (evaluation.skills) {
        setSkills({ ...evaluation.skills });
      } else {
        setSkills({ ataque: 5, passe: 5, defesa: 5, saque: 5, bloqueio: 5, toque: 5, skill: 5 });
      }

      if (evaluation.setter_skills) {
        setSetterSkills({ ...evaluation.setter_skills });
      } else {
        setSetterSkills({
          toque_coordenacao: 5, controle_bola: 5, agilidade_agressividade: 5,
          compreensao_jogo: 5, visao_jogo: 5, tomada_decisao: 5, comunicacao: 5, atitude_mental: 5,
        });
      }
    }
  }, [evaluation]);

  const handleSave = async () => {
    if (!evaluation) return;
    setIsSaving(true);
    try {
      const payload = {
        profile_type: profileType,
        evaluator_id: evaluatorId,
      };
      if (profileType === "levantador") {
        payload.setter_skills = setterSkills;
      } else {
        payload.skills = skills;
      }

      await axios.put(`${API}/evaluations/${evaluation.id}`, payload);
      toast.success("Avaliação atualizada com sucesso");
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar avaliação");
    } finally {
      setIsSaving(false);
    }
  };

  const updateSkill = (key, value) => {
    setSkills(prev => ({ ...prev, [key]: value }));
  };

  const updateSetterSkill = (key, value) => {
    setSetterSkills(prev => ({ ...prev, [key]: value }));
  };

  const currentSkills = profileType === "levantador" ? setterSkills : skills;
  const currentLabels = profileType === "levantador" ? SETTER_SKILL_LABELS : SKILL_LABELS;
  const updateFn = profileType === "levantador" ? updateSetterSkill : updateSkill;

  const avg = Object.values(currentSkills).length > 0
    ? (Object.values(currentSkills).reduce((a, b) => a + b, 0) / Object.values(currentSkills).length).toFixed(1)
    : "0.0";

  if (!evaluation) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-evaluation-dialog">
        <DialogHeader>
          <DialogTitle>Editar Avaliação</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Profile Type */}
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Tipo de Avaliação
            </Label>
            <Select value={profileType} onValueChange={setProfileType}>
              <SelectTrigger className="bg-input/50" data-testid="edit-profile-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="jogador">Jogador</SelectItem>
                <SelectItem value="levantador">Levantador</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Evaluator */}
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Avaliador
            </Label>
            <Select value={evaluatorId} onValueChange={setEvaluatorId}>
              <SelectTrigger className="bg-input/50" data-testid="edit-evaluator-select">
                <SelectValue placeholder="Selecione o avaliador" />
              </SelectTrigger>
              <SelectContent>
                {(evaluators || []).map(ev => (
                  <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Average */}
          <div className="text-center p-3 bg-primary/10 rounded-lg border border-primary/30">
            <span className="text-xs text-muted-foreground uppercase tracking-widest">Média</span>
            <p className="text-3xl font-black text-primary">{avg}</p>
          </div>

          {/* Skills Sliders */}
          <div className="space-y-4">
            {Object.entries(currentLabels).map(([key, label]) => (
              <div key={key} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">{label}</Label>
                  <span className="text-sm font-bold text-primary w-10 text-right">
                    {(currentSkills[key] || 0).toFixed(1)}
                  </span>
                </div>
                <Slider
                  value={[currentSkills[key] || 0]}
                  min={0}
                  max={10}
                  step={0.5}
                  onValueChange={([val]) => updateFn(key, val)}
                  data-testid={`edit-skill-${key}`}
                />
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving} data-testid="save-evaluation-btn">
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
